import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, Divider,
  IconButton, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Lightbulb as HintIcon,
} from '@mui/icons-material';
import CodeEditor from './CodeEditor';
import { runPython, getPyodide, CHART_SNIPPET } from '../hooks/usePyodide';
import { runSql } from '../hooks/useSqlJs';
import { runDuckDb, getDuckDb } from '../hooks/useDuckDb';

const OutputPanel = ({ output, error, chart }) => {
  const hasOutput = output?.trim() || error?.trim() || chart;
  if (!hasOutput) return null;
  return (
    <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#111', borderBottom: '1px solid #333' }}>
        <Typography variant="caption" sx={{ color: '#888', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Output
        </Typography>
      </Box>
      <Box sx={{ bgcolor: '#0d0d0d', p: 2 }}>
        {chart && (
          <Box sx={{ mb: 1 }}>
            <img
              src={`data:image/png;base64,${chart}`}
              alt="chart output"
              style={{ maxWidth: '100%', borderRadius: 4 }}
            />
          </Box>
        )}
        {output?.trim() && (
          <Typography
            component="pre"
            sx={{
              m: 0,
              fontFamily: 'monospace',
              fontSize: '0.82rem',
              color: '#98c379',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {output}
          </Typography>
        )}
        {error?.trim() && (
          <Typography
            component="pre"
            sx={{
              m: 0,
              mt: output?.trim() ? 1 : 0,
              fontFamily: 'monospace',
              fontSize: '0.82rem',
              color: '#e06c75',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const RUNTIME_READY = {};  // tracks which runtimes have booted

const CodeRunner = ({
  value = '',
  onChange,
  language = 'python',
  placeholder = '',
  minRows = 12,
  label,
  hint,
  onOutputChange,
  sampleSql = '',
  csvData = '',
  setupSql = '',
}) => {
  const theme = useTheme();
  const [running, setRunning] = useState(false);
  const [booting, setBooting] = useState(false);
  const [result, setResult] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [pyError, setPyError] = useState('');

  const handleRun = async () => {
    if (!value.trim()) return;
    setRunning(true);
    setBooting(false);
    setResult(null);
    setPyError('');
    try {
      let res;

      if (language === 'sql') {
        res = await runSql(value, sampleSql);

      } else if (language === 'duckdb') {
        if (!RUNTIME_READY.duckdb) {
          setBooting(true);
          await getDuckDb();
          RUNTIME_READY.duckdb = true;
          setBooting(false);
        }
        res = await runDuckDb(value, csvData, setupSql);

      } else {
        if (!RUNTIME_READY.python) {
          setBooting(true);
          await getPyodide();
          RUNTIME_READY.python = true;
          setBooting(false);
        }
        res = await runPython(value);
      }

      setResult(res);
      onOutputChange?.({ code: value, output: res.output, chart: res.chart || '', error: res.error });
    } catch (e) {
      setPyError(e.message);
    } finally {
      setRunning(false);
      setBooting(false);
    }
  };

  const canRun = language === 'python' || language === 'sql' || language === 'duckdb';

  return (
    <Box>
      <CodeEditor
        value={value}
        onChange={onChange}
        language={language}
        placeholder={placeholder}
        minRows={minRows}
        label={label}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        {canRun && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CircularProgress size={14} color="inherit" sx={{ display: running ? 'block' : 'none' }} />}
            onClick={handleRun}
            disabled={running || !value.trim()}
            sx={{ bgcolor: '#2d7a2d', '&:hover': { bgcolor: '#246024' }, fontFamily: 'monospace' }}
          >
            {booting
              ? `Loading ${language === 'duckdb' ? 'DuckDB' : 'Python'}…`
              : running ? 'Running…' : 'Run'}
          </Button>
        )}
        {!canRun && (
          <Typography variant="caption" color="text.secondary">
            {language.toUpperCase()} — paste your code and describe the output below
          </Typography>
        )}
        {(language === 'sql' || language === 'duckdb') && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {language === 'duckdb'
              ? 'DuckDB WASM · analytical SQL · CSV/Parquet support'
              : 'SQLite WASM · lightweight SQL'}
          </Typography>
        )}
        {hint && (
          <Tooltip title={showHint ? 'Hide hint' : 'Show chart hint'}>
            <IconButton size="small" onClick={() => setShowHint((v) => !v)}>
              <HintIcon fontSize="small" sx={{ color: showHint ? 'warning.main' : 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {showHint && (
        <Collapse in={showHint}>
          <Box
            sx={{
              mt: 1, p: 1.5,
              bgcolor: alpha(theme.palette.warning.main, 0.07),
              border: 1, borderColor: alpha(theme.palette.warning.main, 0.3),
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="warning.main">Chart snippet</Typography>
              <Tooltip title="Copy snippet">
                <IconButton size="small" onClick={() => navigator.clipboard?.writeText(CHART_SNIPPET)}>
                  <CopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
              {CHART_SNIPPET}
            </Typography>
          </Box>
        </Collapse>
      )}

      {pyError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {language === 'sql'
            ? `SQL error: ${pyError}`
            : language === 'duckdb'
            ? `DuckDB error: ${pyError}. Check your internet connection — DuckDB loads from CDN on first use.`
            : `Could not start Python runtime: ${pyError}. Check your internet connection — the Python engine loads from CDN on first use (~20 MB).`}
        </Alert>
      )}

      {result && <OutputPanel output={result.output} error={result.error} chart={result.chart} />}

      {!canRun && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Describe your output
          </Typography>
          <Box
            component="textarea"
            onChange={(e) => onOutputChange?.({ code: value, output: e.target.value })}
            placeholder="Paste or describe the result of running this code…"
            rows={3}
            sx={{
              display: 'block', width: '100%', boxSizing: 'border-box',
              mt: 0.5, p: 1.5, fontFamily: 'inherit', fontSize: '0.875rem',
              border: '1px solid', borderColor: 'divider', borderRadius: 1,
              resize: 'vertical', outline: 'none',
              '&:focus': { borderColor: 'primary.main' },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CodeRunner;
